/**
 * What a letter to a home would carry, by the LETTER'S OWN function (Oct 2026).
 *
 * The mailing export prints an address with `formatPropertyAddress` (models/letterAddress.js).
 * Two places on the pages showed "the address" some other way:
 *
 *  - the small label icon beside an address in the property table, titled "Mailing Address
 *    Preview", was built by another function (utils/addressFormatter.js), which does not read
 *    the Land Registry address the letter prints from. A preview that is not the letter;
 *  - the Edit box writes the one-line address, the town and the postcode. The letter is
 *    printed from the Land Registry record where there is one, else from the address in
 *    parts (flat, number, name, street, locality), and only as a last resort from the one
 *    line. So for most homes, an import's included, an edited address changed the page and
 *    NOT the letter, and nothing said so.
 *
 * Nothing here decides what the letter prints: it asks the letter's function and reports.
 * ⚠️ Which source the letter reads is found by ASKING it (does the print change when the one
 * line changes?), never by copying its branches here, where they would drift.
 */
import { formatPropertyAddress } from '../models/letterAddress';

const text = (v) => (typeof v === 'string' ? v.trim() : '');

/**
 * The lines a letter would carry, top to bottom, town and postcode last.
 * @returns {string[]|null} null when the letter's function could not print the record at all
 *   (it throws on a number where it expects text: such a record fails a whole export)
 */
export function letterLines(property) {
  if (!property || typeof property !== 'object') return [];
  let f;
  try { f = formatPropertyAddress(property); } catch (e) { return null; }
  if (!f) return [];
  return [f.address1, f.address2, f.address3, f.town, f.postcode].map(text).filter(Boolean);
}

const same = (a, b) => Array.isArray(a) && Array.isArray(b) && a.length === b.length && a.every((line, i) => line === b[i]);

/** A mark no real address holds, used only to ask the letter's function whether it reads the one-line address. */
const PROBE = ' ZZPROBEZZ 98765';

/**
 * Does the letter read the record's one-line `address`? Asked of the letter's own function:
 * the one line is changed and the print compared.
 * @returns {boolean|null} null when the letter could not be printed
 */
export function letterReadsOneLine(property) {
  const before = letterLines(property);
  if (before === null) return null;
  const after = letterLines({ ...property, address: `${text(property && property.address)}${PROBE}` });
  if (after === null) return null;
  return !same(before, after);
}

/** What the letter is printed from, in words, when it is not the one-line address. */
export function letterSourceWords(property) {
  if (property && property.landRegistryRecord) return 'its Land Registry address';
  return 'the address in parts that came with it (flat, number, name, street, locality)';
}

/**
 * What an edit in the Edit box would do to the letter. The box writes `address`, `postcode`
 * and `town`, and nothing else of the address.
 * @param {Object} property the record as held
 * @param {{address: string, postcode: string, town: string}} form what the box holds now
 * @returns {{ before, after, cannotPrint, readsOneLine, addressEdited, letterChanges, standing, warning }}
 *   `standing`: said in the box whenever the letter does not read the one line, before anything is typed;
 *   `warning`: said when the address has been edited and the letter would not change for it.
 */
export function editEffect(property, form) {
  const held = property || {};
  const typed = form || {};
  const edited = { ...held, address: text(typed.address), postcode: text(typed.postcode), town: text(typed.town) || null };
  const before = letterLines(held);
  const after = letterLines(edited);
  const cannotPrint = before === null || after === null;
  const readsOneLine = cannotPrint ? null : letterReadsOneLine(held);
  const addressEdited = text(typed.address) !== text(held.address);
  const letterChanges = cannotPrint ? null : !same(before, after);
  const from = letterSourceWords(held);
  const standing = readsOneLine === false
    ? `This home's mailshot is printed from ${from}, which this box does not edit. Changing the address line here changes what the pages show, not what the mailshot carries. Only a change of postcode${held.landRegistryRecord ? '' : ' or town'} reaches the mailshot.`
    : null;
  const warning = !cannotPrint && addressEdited && !letterChanges
    ? 'The address line has been changed, and THE MAILSHOT WILL NOT CHANGE: it will still carry the lines shown here.'
    : null;
  return { before, after, cannotPrint, readsOneLine, addressEdited, letterChanges, standing, warning };
}
